import {
    getGradientDirectionDegrees,
    isGradientColorScheme,
} from './gradientDirection';

const DARK_BASE = {
    headerColor: 'text-white',
    iconColor: 'text-white',
    textColor: 'text-white',
    boxTextColor: 'text-white',
    thirdButton: 'outlineDark',
    secondaryButton: 'ghost',
    taglineColor: 'text-white',
    linkColor: 'text-white font-bold hover:opacity-80',
};

const LIGHT_PRIMARY_BASE = {
    backgroundColor: 'bg-white',
    headerColor: 'text-neutral-800',
    iconColor: 'text-primary-700',
    textColor: 'text-neutral-800',
    boxTextColor: 'text-neutral-800',
    thirdButton: 'outline',
    secondaryButton: 'secondary',
    taglineColor: 'text-primary-700',
    linkColor: 'text-primary-700 hover:opacity-80',
};

const LIGHT_SECONDARY_BASE = {
    backgroundColor: 'bg-white',
    headerColor: 'text-neutral-800',
    iconColor: 'text-secondary-700',
    textColor: 'text-neutral-800',
    boxTextColor: 'text-neutral-800',
    thirdButton: 'outline',
    secondaryButton: 'secondary',
    taglineColor: 'text-secondary-700',
    linkColor: 'text-secondary-700 hover:opacity-80',
};

const GRADIENT_STYLE_CONFIG = {
    gradient: {
        backgroundFrom: 'var(--primary-800)',
        backgroundTo: 'var(--secondary-800)',
        overlayFrom: 'var(--primary-800)',
        overlayTo: 'var(--secondary-800)',
    },
    'dark-gradient': {
        backgroundFrom: 'var(--primary-900)',
        backgroundTo: 'var(--secondary-900)',
        overlayFrom: 'var(--primary-900)',
        overlayTo: 'var(--secondary-900)',
    },
    'light-gradient': {
        backgroundFrom: 'var(--primary-100)',
        backgroundTo: 'var(--secondary-100)',
        overlayFrom: 'var(--primary-100)',
        overlayTo: 'var(--secondary-100)',
    },
    'light-primary-gradient': {
        backgroundFrom: 'var(--primary-100)',
        backgroundTo: 'var(--primary-600)',
        overlayFrom: 'var(--primary-100)',
        overlayTo: 'var(--primary-400)',
    },
    'primary-gradient': {
        backgroundFrom: 'var(--primary-600)',
        backgroundTo: 'var(--primary-800)',
        overlayFrom: 'var(--primary-600)',
        overlayTo: 'var(--primary-900)',
    },
    'dark-primary-gradient': {
        backgroundFrom: 'var(--primary-800)',
        backgroundTo: 'var(--primary-950)',
        overlayFrom: 'var(--primary-800)',
        overlayTo: 'var(--primary-950)',
    },
    'dark-secondary-gradient': {
        backgroundFrom: 'var(--secondary-800)',
        backgroundTo: 'var(--secondary-950)',
        overlayFrom: 'var(--secondary-800)',
        overlayTo: 'var(--secondary-950)',
    },
    'secondary-gradient': {
        backgroundFrom: 'var(--secondary-600)',
        backgroundTo: 'var(--secondary-800)',
        overlayFrom: 'var(--secondary-600)',
        overlayTo: 'var(--secondary-900)',
    },
    'light-secondary-gradient': {
        backgroundFrom: 'var(--secondary-100)',
        backgroundTo: 'var(--secondary-600)',
        overlayFrom: 'var(--secondary-100)',
        overlayTo: 'var(--secondary-400)',
    },
    'mid-gradient': {
        backgroundFrom: 'var(--primary-600)',
        backgroundTo: 'var(--secondary-600)',
        overlayFrom: 'var(--primary-600)',
        overlayTo: 'var(--secondary-600)',
    },
};

export function getColorScheme(scheme = 'default', gradientDirection = 'down') {
    const schemes = {
        dark: {
            ...DARK_BASE,
            backgroundColor: 'bg-neutral-800',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-neutral-800 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        light: {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-neutral-100',
            headerColor: 'text-neutral-700',
            overlayColor: 'bg-neutral-100 opacity-70',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        primary: {
            ...DARK_BASE,
            backgroundColor: 'bg-primary-800',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-primary-800 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        secondary: {
            ...DARK_BASE,
            backgroundColor: 'bg-secondary-800',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-secondary-800 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'mid-secondary': {
            ...DARK_BASE,
            backgroundColor: 'bg-secondary-600',
            badgeColor: 'filter: grayscale',
            overlayColor: 'bg-secondary-600 opacity-80',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'mid-primary': {
            ...DARK_BASE,
            backgroundColor: 'bg-primary-600',
            badgeColor: 'filter: grayscale',
            overlayColor: 'bg-primary-600 opacity-80',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'dark-primary': {
            ...DARK_BASE,
            backgroundColor: 'bg-primary-900',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-primary-900 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'darker-primary': {
            ...DARK_BASE,
            backgroundColor: 'bg-primary-950',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-primary-950 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'dark-secondary': {
            ...DARK_BASE,
            backgroundColor: 'bg-secondary-900',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-secondary-900 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'darker-secondary': {
            ...DARK_BASE,
            backgroundColor: 'bg-secondary-950',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor: 'bg-secondary-950 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'light-secondary': {
            ...LIGHT_SECONDARY_BASE,
            backgroundColor: 'bg-secondary-100',
            overlayColor:
                'bg-gradient-to-b from-secondary-100 to-secondary-100 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'light-primary': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-primary-100',
            overlayColor: 'bg-primary-100 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'box-primary': {
            backgroundColor: 'bg-white',
            headerColor: 'text-neutral-800',
            iconColor: 'text-white',
            textColor: 'text-neutral-800',
            boxColor: 'bg-primary-800',
            boxTextColor: 'text-white',
            thirdButton: 'outline',
            secondaryButton: 'secondary',
            taglineColor: 'text-primary-800',
            boxThirdButton: 'outlineDark',
            boxSecondaryButton: 'ghost',
            linkColor: 'text-white font-bold hover:opacity-80',
        },

        'box-secondary': {
            backgroundColor: 'bg-white',
            headerColor: 'text-neutral-800',
            iconColor: 'text-white',
            textColor: 'text-neutral-800',
            boxColor: 'bg-secondary-800',
            boxTextColor: 'text-white',
            thirdButton: 'outline',
            secondaryButton: 'secondary',
            taglineColor: 'text-secondary-800',
            boxThirdButton: 'outlineDark',
            boxSecondaryButton: 'ghost',
            linkColor: 'text-white font-bold hover:opacity-80',
        },

        'box-border-primary': {
            ...LIGHT_PRIMARY_BASE,
            boxColor: 'bg-white',
            boxTextColor: 'text-neutral-800',
            boxBorder: 'border-2 border-solid border-primary-700',
            taglineColor: 'text-primary-800',
            boxThirdButton: 'outline',
            boxSecondaryButton: 'secondary',
        },

        'box-border-secondary': {
            ...LIGHT_SECONDARY_BASE,
            boxColor: 'bg-white',
            boxTextColor: 'text-neutral-800',
            boxBorder: 'border-2 border-solid border-secondary-700',
            taglineColor: 'text-secondary-800',
            boxThirdButton: 'outline',
            boxSecondaryButton: 'secondary',
        },

        'white-secondary': {
            ...LIGHT_SECONDARY_BASE,
            backgroundColor: 'bg-white',
            overlayColor: 'bg-white opacity-70',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        gradient: {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-800 from-5% to-secondary-800 to-95%',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor:
                'bg-gradient-to-b from-primary-800 to-secondary-800 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'dark-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-900 from-5% to-secondary-900 to-95%',
            badgeColor: 'opacity-100 mix-blend-screen grayscale invert',
            overlayColor:
                'bg-gradient-to-b from-primary-900 to-secondary-900 opacity-80',
            exitButton: 'text-white/50 hover:text-white',
            bgOverlay: 'bg-white/20 backdrop-blur-sm',
        },

        'light-gradient': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-100 from-5% to-secondary-100 to-95%',
            taglineColor: 'text-neutral-800',
            overlayColor:
                'bg-gradient-to-b from-primary-100 to-secondary-100 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'light-primary-gradient': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-100 from-5% to-primary-600 to-95%',
            taglineColor: 'text-neutral-800',
            overlayColor:
                'bg-gradient-to-b from-primary-100 to-primary-400 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
            linkColor: 'text-neutral-800 hover:opacity-80',
        },

        'primary-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-600 from-5% to-primary-800 to-95%',
            overlayColor:
                'bg-gradient-to-b from-primary-600 to-primary-900 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'dark-primary-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-800 from-5% to-primary-950 to-95%',
            overlayColor:
                'bg-gradient-to-b from-primary-800 to-primary-950 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'dark-secondary-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-secondary-800 from-5% to-secondary-950 to-95%',
            overlayColor:
                'bg-gradient-to-b from-secondary-800 to-secondary-950 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'secondary-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-secondary-600 from-5% to-secondary-800 to-95%',
            overlayColor:
                'bg-gradient-to-b from-secondary-600 to-secondary-900 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'light-secondary-gradient': {
            ...LIGHT_SECONDARY_BASE,
            backgroundColor:
                'bg-gradient-to-b from-secondary-100 from-5% to-secondary-600 to-95%',
            taglineColor: 'text-neutral-800',
            overlayColor:
                'bg-gradient-to-b from-secondary-100 to-secondary-400 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
            linkColor: 'text-white font-bold hover:opacity-80',
        },

        'mid-gradient': {
            ...DARK_BASE,
            backgroundColor:
                'bg-gradient-to-b from-primary-600 from-5% to-secondary-600 to-95%',
            overlayColor:
                'bg-gradient-to-b from-primary-600 to-secondary-600 opacity-80',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        white: {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            boxBorder: 'border-none',
            overlayColor: 'bg-white opacity-70',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'white-primary': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            boxBorder: 'border-none',
            overlayColor: 'bg-white opacity-70',
            bgOverlay: 'bg-black/10 backdrop-blur-sm',
        },

        'no-filter': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            taglineColor: 'text-neutral-800',
            overlayColor:
                'lg:bg-none bg-gradient-to-b from-neutral-100 to-white opacity-70',
            bgOverlay: 'lg:bg-none bg-black/10 backdrop-blur-sm',
        },

        clear: {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            taglineColor: 'text-neutral-800',
            bgOverlay: 'lg:bg-none bg-black/10 backdrop-blur-sm',
        },

        'no-filter-dark': {
            ...DARK_BASE,
            backgroundColor: 'bg-neutral-800',
            gradientColors:
                'bg-gradient-to-b from-neutral-800 via-neutral-900 to-neutral-950 lg:opacity-0 opacity-70',
            exitButton: 'text-white/50 hover:text-white',
        },

        blur: {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            boxThirdButton: 'outline',
            boxSecondaryButton: 'secondary',
            boxColor:
                'bg-blur bg-white/50 inset-0 backdrop-blur-sm backdrop-filter',
        },

        'blur-primary': {
            ...LIGHT_PRIMARY_BASE,
            backgroundColor: 'bg-white',
            boxTextColor: 'text-neutral-900',
            boxThirdButton: 'outline',
            boxSecondaryButton: 'secondary',
            boxColor:
                'bg-blur-primary-tint inset-0 backdrop-blur-md backdrop-filter',
            bgOverlay: 'bg-blur-primary-tint backdrop-blur-md',
        },

        'blur-secondary': {
            ...LIGHT_SECONDARY_BASE,
            backgroundColor: 'bg-white',
            boxTextColor: 'text-neutral-900',
            boxThirdButton: 'outline',
            boxSecondaryButton: 'secondary',
            boxColor:
                'bg-blur-secondary-tint inset-0 backdrop-blur-md backdrop-filter',
            bgOverlay: 'bg-blur-secondary-tint backdrop-blur-md',
        },

        'dark-blur': {
            ...DARK_BASE,
            backgroundColor: 'bg-neutral-800',
            iconColor: 'text-primary-700',
            boxTextColor: 'text-white',
            boxColor:
                'inset-0 bg-neutral-700/70 bg-blur inset-0 backdrop-blur-sm backdrop-filter',
            boxThirdButton: 'outlineDark',
            boxSecondaryButton: 'ghost',
            exitButton: 'text-white/50 hover:text-white',
        },
    };

    const normalizedScheme = String(scheme ?? '').trim();
    const selected = schemes[normalizedScheme] || schemes['white'];

    if (!isGradientColorScheme(normalizedScheme)) {
        return selected;
    }

    const config = GRADIENT_STYLE_CONFIG[normalizedScheme];
    const angle = getGradientDirectionDegrees(gradientDirection);

    return {
        ...selected,
        backgroundStyle: {
            backgroundImage: `linear-gradient(${angle}, ${config.backgroundFrom} 5%, ${config.backgroundTo} 95%)`,
        },
        overlayStyle: {
            backgroundImage: `linear-gradient(${angle}, ${config.overlayFrom}, ${config.overlayTo})`,
        },
    };
}

/* ------------------------- Map color schemes ------------------------- */

const mapSchemes = {
    light: {
        mapBackgroundClass: 'bg-white-100',
        mapBlendEffect: 'mix-blend-screen grayscale',
    },
    default: null,

    dark: {
        mapBackgroundClass: 'bg-neutral-900',
        mapBlendEffect: 'mix-blend-screen invert grayscale',
    },
    primary: {
        mapBackgroundClass: 'bg-primary-900',
        mapBlendEffect: 'mix-blend-screen invert grayscale',
    },
    'light-primary': {
        mapBackgroundClass: 'bg-primary-900',
        mapBlendEffect: 'mix-blend-screen grayscale',
    },
    'full-color': {
        mapBackgroundClass: 'bg-secondary-900',
        mapBlendEffect: '',
    },
    secondary: {
        mapBackgroundClass: 'bg-secondary-900',
        mapBlendEffect: 'mix-blend-screen invert grayscale',
    },
    'light-secondary': {
        mapBackgroundClass: 'bg-secondary-900',
        mapBlendEffect: 'mix-blend-screen grayscale',
    },
    gradient: {
        mapBackgroundClass:
            'bg-gradient-to-b from-primary-800 from-5% to-secondary-800 to-95%',
        mapBlendEffect: 'mix-blend-screen invert grayscale',
    },
    'light-gradient': {
        mapBackgroundClass:
            'bg-gradient-to-b from-primary-800 from-5% to-secondary-800 to-95%',
        mapBlendEffect: 'mix-blend-screen grayscale',
    },
};

mapSchemes.default = mapSchemes.light;

export function getMapColorScheme(name = 'light') {
    const key = String(name || '').trim() || 'light';
    return mapSchemes[key] || mapSchemes.light;
}
